import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Lock, X } from 'lucide-react';
import {
  getStatus, skipCheck, uploadMonthFile, removeUpload, getWorklist, saveName, saveNamesBulk,
  addCheck, applyCheck, resetName, setAside, reopenCheck, getMenuItems,
} from './salesNameCheck/salesNameCheckApi';
import { ColumnConfirmModal, MonthPickerModal, OverlapModal } from './salesNameCheck/UploadPrompts';
import UploadCard from './salesNameCheck/UploadCard';
import WorklistHeader from './salesNameCheck/WorklistHeader';
import NameRow from './salesNameCheck/NameRow';
import ConflictModal from './salesNameCheck/ConflictModal';
import ContinueScreen from './salesNameCheck/ContinueScreen';
import {
  GROUPS, SKIP_REASONS, blankDecision, buildSkipReason, decisionBody, firstOpenRow,
  hasAcceptedExtension, isConflict, isUndecided, money, shortDate,
} from './salesNameCheck/sncShared';
import './SalesNameCheck.css';

/**
 * Sales Name Check (spec §12): one month of POS sales, every dish name the POS
 * prints matched to the menu once, before going live (mode 'onboarding') or
 * later from Settings (mode 'reopen'). The server owns the worklist; every
 * click is saved with the row's version, and one Apply writes it all.
 * Nothing on this page moves stock or books sales.
 */

const ROW_SCROLL = { block: 'center' };

function SalesNameCheck({ mode = 'onboarding', onBack, onContinue, onOpenItemMap }) {
  const isReopen = mode === 'reopen';

  const [phase, setPhase] = useState('loading'); // loading | error | locked | upload | worklist | closed
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [meta, setMeta] = useState(null); // { check, uploads, ignoredRows, coverage, progress }
  const [rows, setRows] = useState([]);
  const [closedState, setClosedState] = useState(null); // 'done' | 'skipped'
  const [applySummary, setApplySummary] = useState(null);
  const [menu, setMenu] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [prompt, setPrompt] = useState(null); // { type: 'columns'|'month'|'overlap', file, answers, ... }

  const [activeGroup, setActiveGroup] = useState('nomatch');
  const [search, setSearch] = useState('');
  const [panel, setPanel] = useState(null); // { nameId, kind: 'link'|'add'|'skip' }
  const [focusedId, setFocusedId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkReason, setBulkReason] = useState('');
  const [bulkReasonText, setBulkReasonText] = useState('');
  const [rowUi, setRowUi] = useState({}); // nameId → { saving, error, body }
  const [conflict, setConflict] = useState(null); // { row, body, current }
  const [notice, setNotice] = useState(null); // { tone: 'info'|'error', text }
  const [busy, setBusy] = useState(false);

  // Refs, not state, wherever a double click or a stale closure must not slip through.
  const busyRef = useRef(false);
  const uploadingRef = useRef(false);
  const rowUiRef = useRef({});
  const rowsRef = useRef([]);
  const checkVersionRef = useRef(null);
  const scrollTargetRef = useRef(null);
  const summaryTimerRef = useRef(null);
  const keyHandlerRef = useRef(null);
  const menuLoadedRef = useRef(false);

  rowsRef.current = rows;

  const takeVersion = (r) => {
    if (r && r.version != null && Number.isFinite(Number(r.version))) checkVersionRef.current = Number(r.version);
  };

  const patchRowUi = (nameId, value) => {
    const next = { ...rowUiRef.current };
    if (value) next[nameId] = value;
    else delete next[nameId];
    rowUiRef.current = next;
    setRowUi(next);
  };

  const replaceRows = (updated) => {
    if (!updated || updated.length === 0) return;
    const byId = new Map(updated.map((r) => [r.nameId, r]));
    setRows((prev) => prev.map((r) => byId.get(r.nameId) || r));
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  const loadWorklist = useCallback(async ({ scrollToOpen = false } = {}) => {
    const w = await getWorklist();
    if (!w.ok) {
      if (w.code === 'NO_OPEN_CHECK') {
        setMeta(null);
        setRows([]);
        setPhase('upload');
        return;
      }
      setLoadError(w.error);
      setPhase('error');
      return;
    }
    const names = Array.isArray(w.names) ? w.names : [];
    setMeta({
      check: w.check || null,
      uploads: Array.isArray(w.uploads) ? w.uploads : [],
      ignoredRows: Array.isArray(w.ignoredRows) ? w.ignoredRows : [],
      coverage: w.coverage || null,
      progress: w.progress || null,
    });
    setRows(names);
    checkVersionRef.current = w.check && w.check.version != null ? Number(w.check.version) : null;
    rowUiRef.current = {};
    setRowUi({});
    setSelected(new Set());
    setPanel(null);

    const state = w.check && w.check.state;
    if (state === 'done' || state === 'skipped') {
      setClosedState(state);
      setPhase('closed');
      return;
    }
    if (!Array.isArray(w.uploads) || w.uploads.length === 0) {
      setPhase('upload');
      return;
    }
    setPhase('worklist');
    if (scrollToOpen) {
      // Resume (spec §12): the first undecided row in the first group that has one.
      const target = firstOpenRow(names);
      if (target) {
        setActiveGroup(target.group);
        setFocusedId(target.nameId);
        scrollTargetRef.current = target.nameId;
      } else {
        const g = GROUPS.find((x) => names.some((n) => n.group === x.key));
        setActiveGroup(g ? g.key : 'matched');
      }
    }
  }, []);

  const loadStatus = useCallback(async () => {
    setPhase('loading');
    setLoadError('');
    const s = await getStatus();
    if (!s.ok) {
      setLoadError(s.error);
      setPhase('error');
      return;
    }
    setStatus(s);
    if (s.state === 'in_progress') {
      await loadWorklist({ scrollToOpen: true });
      return;
    }
    if (s.state === 'done' || s.state === 'skipped') {
      setClosedState(s.state);
      setPhase('closed');
      return;
    }
    setPhase(s.locked ? 'locked' : 'upload');
  }, [loadWorklist]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (phase !== 'worklist' || menuLoadedRef.current) return;
    menuLoadedRef.current = true;
    getMenuItems().then((m) => {
      if (m.ok) setMenu(m.data || m.items || []);
    });
  }, [phase]);

  useEffect(() => {
    const id = scrollTargetRef.current;
    if (!id || phase !== 'worklist') return;
    const el = document.getElementById(`snc-row-${id}`);
    if (el) {
      scrollTargetRef.current = null;
      el.scrollIntoView(ROW_SCROLL);
    }
  }, [phase, rows, activeGroup]);

  // Coverage and progress are computed on the server; refresh them shortly after the last save.
  const scheduleSummaryRefresh = useCallback(() => {
    if (summaryTimerRef.current) clearTimeout(summaryTimerRef.current);
    summaryTimerRef.current = setTimeout(async () => {
      summaryTimerRef.current = null;
      const w = await getWorklist();
      if (!w.ok) return;
      setMeta((prev) => (prev ? { ...prev, coverage: w.coverage || prev.coverage, progress: w.progress || prev.progress } : prev));
    }, 1500);
  }, []);

  useEffect(() => () => {
    if (summaryTimerRef.current) clearTimeout(summaryTimerRef.current);
  }, []);

  // ── Upload (spec §6) ───────────────────────────────────────────────────────

  const hasOpenCheck = Boolean(
    (meta && meta.check && meta.check.state === 'in_progress') || (status && status.state === 'in_progress'),
  );

  const runUpload = async (file, answers = {}, { reopened = false } = {}) => {
    if (!file || uploadingRef.current) return;
    if (!hasAcceptedExtension(file.name)) {
      setUploadError('Please upload an .xlsx, .xls or .csv file.');
      return;
    }
    uploadingRef.current = true;
    setUploading(true);
    setUploadError('');
    let r;
    try {
      r = await uploadMonthFile({ file, ...answers });
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
    if (r.ok) {
      setPrompt(null);
      takeVersion(r);
      await loadWorklist({ scrollToOpen: true });
      return;
    }
    switch (r.code) {
      case 'NEEDS_COLUMNS':
        setPrompt({ type: 'columns', file, answers, sample: r.sample, guesses: r.guesses, headerRowIndex: r.headerRowIndex });
        return;
      case 'NEEDS_MONTH':
        setPrompt({ type: 'month', file, answers });
        return;
      case 'DATE_OVERLAP':
        setPrompt({ type: 'overlap', file, answers, existing: r.existing });
        return;
      case 'DUPLICATE_FILE':
        setPrompt(null);
        setUploadError(`This file was already uploaded on ${shortDate(r.uploadedAt)}.`);
        return;
      case 'ITEM_MAPPING_INCOMPLETE':
        setPrompt(null);
        setStatus((prev) => ({ ...(prev || {}), locked: true, lockedReason: r.error }));
        setPhase('locked');
        return;
      case 'NO_OPEN_CHECK':
        if (isReopen && !reopened) {
          // Spec §6.7/§10.5: a live business (or a closed check) uploads into a new reopen-mode check.
          const o = await reopenCheck();
          if (o.ok) {
            takeVersion(o);
            await runUpload(file, answers, { reopened: true });
            return;
          }
          setPrompt(null);
          setUploadError(o.error);
          return;
        }
        setPrompt(null);
        setUploadError('This check is closed. Open Settings → Sales Name Check to run it again.');
        return;
      case 'UNREADABLE':
        setPrompt(null);
        setUploadError(r.error || 'This file could not be read. Upload the item-wise sales export as .xlsx, .xls or .csv.');
        return;
      default:
        setPrompt(null);
        setUploadError(r.error);
    }
  };

  const handleRemoveUpload = async (upload) => {
    if (busyRef.current) return;
    if (!window.confirm(`Remove ${upload.fileName}? Its sales leave the totals. Decisions stay for names still in other files.`)) return;
    busyRef.current = true;
    setBusy(true);
    let r;
    try {
      r = await removeUpload(upload.uploadId);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
    if (!r.ok) {
      setNotice({ tone: 'error', text: r.error });
      return;
    }
    takeVersion(r);
    await loadWorklist({ scrollToOpen: true });
  };

  // ── Leaving the step ───────────────────────────────────────────────────────

  const handleSkipStep = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setUploadError('');
    let r;
    try {
      r = hasOpenCheck ? await setAside() : await skipCheck();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
    if (!r.ok && r.code === 'CHECK_EXISTS') {
      // Someone (another tab or device) started a check since this page loaded: show it, never discard it.
      setNotice({ tone: 'info', text: 'A Sales Name Check was already started, perhaps on another device. It is shown below.' });
      await loadStatus();
      return;
    }
    if (!r.ok) {
      setUploadError(r.error);
      return;
    }
    if (onContinue) onContinue();
  };

  const handleSetAside = async () => {
    if (busyRef.current) return;
    if (!window.confirm('Set Sales Name Check aside? Decisions that were not applied are discarded and nothing new is remembered.')) return;
    busyRef.current = true;
    setBusy(true);
    let r;
    try {
      r = await setAside();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
    if (!r.ok) {
      setNotice({ tone: 'error', text: r.error });
      return;
    }
    if (isReopen) {
      if (onBack) onBack();
      return;
    }
    if (onContinue) onContinue();
  };

  const handleRunAgain = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    let r;
    try {
      r = await reopenCheck();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
    if (!r.ok) {
      setNotice({ tone: 'error', text: r.error });
      return;
    }
    setApplySummary(null);
    setNotice(null);
    await loadStatus();
  };

  // ── Saving decisions (spec §8.1) ───────────────────────────────────────────

  const saveRow = async (row, body, { expectedVersion = null } = {}) => {
    const nameId = row.nameId;
    if (rowUiRef.current[nameId] && rowUiRef.current[nameId].saving) return { ok: false, code: 'BUSY' };
    patchRowUi(nameId, { saving: true, error: null, body });
    const r = await saveName(nameId, { ...body, expectedVersion: expectedVersion != null ? expectedVersion : row.rowVersion });
    if (r.ok && r.name) {
      replaceRows([r.name]);
      takeVersion(r);
      patchRowUi(nameId, null);
      scheduleSummaryRefresh();
      return r;
    }
    if (r.code === 'ROW_CONFLICT' && r.current) {
      patchRowUi(nameId, { saving: false, error: 'Not saved — someone else changed this name', body });
      setConflict({ row, body, current: r.current });
      return r;
    }
    patchRowUi(nameId, { saving: false, error: `Not saved${r.error ? ` — ${r.error}` : ''}`, body });
    return r;
  };

  const rowBusy = (row) => busyRef.current || Boolean(rowUiRef.current[row.nameId] && rowUiRef.current[row.nameId].saving);

  const togglePanel = (row, kind) => {
    setPanel((p) => (p && p.nameId === row.nameId && p.kind === kind ? null : { nameId: row.nameId, kind }));
  };

  // Clicking the active action again clears it (spec §8.1).
  const clickLink = (row) => {
    if (rowBusy(row)) return;
    setFocusedId(row.nameId);
    if (row.decision && row.decision.action === 'link') {
      setPanel(null);
      saveRow(row, blankDecision());
      return;
    }
    togglePanel(row, 'link');
  };

  const clickAdd = (row) => {
    if (rowBusy(row)) return;
    setFocusedId(row.nameId);
    if (row.decision && row.decision.action === 'add') {
      setPanel(null);
      saveRow(row, blankDecision());
      return;
    }
    togglePanel(row, 'add');
  };

  const clickSkip = (row) => {
    if (rowBusy(row)) return;
    setFocusedId(row.nameId);
    if (row.decision && row.decision.action === 'skip') {
      setPanel(null);
      saveRow(row, blankDecision());
      return;
    }
    if (row.skipReasonRequired) {
      togglePanel(row, 'skip');
      return;
    }
    setPanel(null);
    saveRow(row, { ...blankDecision(), action: 'skip' });
  };

  // ── Apply (spec §8.3) and conflicts (§8.4) ─────────────────────────────────

  const doApply = async (applyMode) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setNotice(null);
    let r;
    try {
      r = await applyCheck({ mode: applyMode, expectedCheckVersion: checkVersionRef.current });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
    if (!r.ok) {
      if (r.code === 'CHECK_CONFLICT') {
        await loadWorklist();
        setNotice({ tone: 'error', text: 'Someone else changed this check after it was opened. The list has been reloaded — look it over and press Apply again.' });
        return;
      }
      if (r.code === 'UNDECIDED') {
        await loadWorklist();
        setNotice({ tone: 'error', text: `${r.count} name${r.count === 1 ? '' : 's'} (${money(r.amount)}) still need a decision.` });
        return;
      }
      setNotice({ tone: 'error', text: r.error });
      return;
    }
    takeVersion(r);
    setApplySummary(r.summary || null);
    if (r.state === 'done') {
      setClosedState('done');
      setPhase('closed');
      return;
    }
    await loadWorklist();
    const s = r.summary || {};
    setNotice({
      tone: 'info',
      text: `Applied: ${s.saved || 0} saved · ${s.priceDecides || 0} matched by price · ${s.added || 0} added · ${s.conflicts || 0} conflict${s.conflicts === 1 ? '' : 's'}. Resolve the conflicts below to continue.`,
    });
  };

  // After a conflict is resolved: re-apply only when that cannot push new names to Not in Menu unasked.
  const reapplyAfterResolve = async () => {
    const open = rowsRef.current.filter(isUndecided);
    if (open.length === 0) {
      await doApply('all');
      return;
    }
    if (open.every((r) => r.result && r.result.code === 'left_for_nim')) {
      await doApply('decided_skip_rest');
      return;
    }
    setNotice({ tone: 'info', text: 'Saved. Decide the remaining names, then press Apply.' });
  };

  const resolveReplace = async (row) => {
    let body = null;
    if (row.decision && row.decision.action) body = decisionBody(row);
    else if (row.matchedMenuItem) body = { ...blankDecision(), action: 'link', targetMenuItemId: Number(row.matchedMenuItem.menuItemId) };
    if (!body) return;
    const r = await saveRow(row, { ...body, replaceConfirmed: true });
    if (r.ok) await reapplyAfterResolve();
  };

  const resolveKeepExisting = async (row) => {
    const existing = row.result && row.result.detail && row.result.detail.existing;
    if (!existing) return;
    let body = null;
    if (existing.kind === 'skip') {
      body = { ...blankDecision(), action: 'skip', skipReason: existing.skipReason || null };
    } else if (existing.kind === 'price' && Array.isArray(existing.entries) && existing.entries.length >= 2) {
      body = {
        ...blankDecision(),
        action: 'link',
        priceSplit: existing.entries.map((e) => ({ price: Number(e.price), menuItemId: Number(e.menuItemId) })),
      };
    } else if (existing.menuItemId) {
      body = { ...blankDecision(), action: 'link', targetMenuItemId: Number(existing.menuItemId) };
    }
    if (!body) return;
    const r = await saveRow(row, body);
    if (r.ok) await reapplyAfterResolve();
  };

  const handleResetRow = async (row) => {
    if (rowBusy(row)) return;
    const added = row.createdMenuItemId ? ' The dish it added is removed too if it has no sales yet.' : '';
    if (!window.confirm(`Undo what Apply saved for “${row.displayName}”? Its remembered name is forgotten.${added}`)) return;
    patchRowUi(row.nameId, { saving: true, error: null, body: null });
    const r = await resetName(row.nameId);
    if (!r.ok) {
      patchRowUi(row.nameId, { saving: false, error: `Not undone${r.error ? ` — ${r.error}` : ''}`, body: null });
      return;
    }
    patchRowUi(row.nameId, null);
    if (r.name) replaceRows([r.name]);
    takeVersion(r);
    if (r.state && r.state !== 'in_progress') {
      await loadWorklist();
      return;
    }
    scheduleSummaryRefresh();
  };

  // ── Bulk (spec §12 "Bulk") ─────────────────────────────────────────────────

  const runBulk = async (items, { clearSelection = false } = {}) => {
    if (items.length === 0 || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setNotice(null);
    let r;
    try {
      r = await saveNamesBulk(items);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
    if (!r.ok) {
      setNotice({ tone: 'error', text: `Not saved${r.error ? ` — ${r.error}` : ''}` });
      return;
    }
    const saved = Array.isArray(r.saved) ? r.saved : [];
    const conflicts = Array.isArray(r.conflicts) ? r.conflicts : [];
    const invalid = Array.isArray(r.invalid) ? r.invalid : [];
    replaceRows(saved);
    takeVersion(r);
    if (clearSelection) {
      setSelected(new Set());
      setBulkReason('');
      setBulkReasonText('');
    }
    scheduleSummaryRefresh();
    if (conflicts.length > 0 || invalid.length > 0) {
      const parts = [`${saved.length} saved.`];
      if (conflicts.length > 0) parts.push(`${conflicts.length} changed by someone else — the list was reloaded, check them again.`);
      if (invalid.length > 0) parts.push(`${invalid.length} not saved: ${invalid[0].error}`);
      await loadWorklist();
      setNotice({ tone: 'error', text: parts.join(' ') });
    }
  };

  // ── Derived view ───────────────────────────────────────────────────────────

  const q = search.trim().toLowerCase();
  const searchedRows = useMemo(() => (q
    ? rows.filter((r) => String(r.displayName || '').toLowerCase().includes(q)
      || (r.spellings || []).some((s) => String(s.text || '').toLowerCase().includes(q)))
    : rows), [rows, q]);
  const groupCounts = useMemo(() => GROUPS.reduce((acc, g) => {
    acc[g.key] = searchedRows.filter((r) => r.group === g.key).length;
    return acc;
  }, {}), [searchedRows]);
  const visibleRows = useMemo(() => searchedRows.filter((r) => r.group === activeGroup), [searchedRows, activeGroup]);

  const undecidedRows = rows.filter(isUndecided);
  const undecided = {
    count: undecidedRows.length,
    amount: undecidedRows.reduce((sum, r) => sum + Number(r.amount || 0), 0),
  };
  const conflictCount = rows.filter(isConflict).length;
  const unsavedCount = Object.values(rowUi).filter((u) => u && (u.saving || u.error)).length;

  // Dishes with sales this month (spec §7 "claimed"); every other dish is "0 sales this month".
  const claimedIds = useMemo(() => {
    const ids = new Set();
    rows.forEach((r) => {
      if (r.matchedMenuItem && r.matchedMenuItem.menuItemId != null) ids.add(Number(r.matchedMenuItem.menuItemId));
      const d = r.decision;
      if (d && d.action === 'link' && d.targetMenuItemId != null) ids.add(Number(d.targetMenuItemId));
      if (d && Array.isArray(d.priceSplit)) d.priceSplit.forEach((p) => ids.add(Number(p.menuItemId)));
    });
    return ids;
  }, [rows]);
  const unsoldIds = useMemo(
    () => new Set(menu.map((m) => Number(m.menu_item_id ?? m.id)).filter((id) => !claimedIds.has(id))),
    [menu, claimedIds],
  );
  const labelFor = useCallback((id) => (unsoldIds.has(Number(id)) ? '0 sales this month' : null), [unsoldIds]);

  const acceptable = rows.filter((r) => r.group === 'confident' && isUndecided(r) && r.suggestions && r.suggestions[0]);
  const selectedRows = rows.filter((r) => selected.has(r.nameId));
  const bulkNeedsReason = selectedRows.some((r) => r.skipReasonRequired);
  const bulkSkipReason = buildSkipReason(bulkReason, bulkReasonText);
  const canBulkSkip = selectedRows.length > 0 && !busy && (!bulkNeedsReason || Boolean(bulkSkipReason));

  const handleAcceptAll = () => runBulk(acceptable.map((r) => ({
    nameId: r.nameId,
    ...blankDecision(),
    action: 'link',
    targetMenuItemId: Number(r.suggestions[0].menuItemId),
    expectedVersion: r.rowVersion,
  })));

  const handleSkipSelected = () => {
    if (!canBulkSkip) return;
    runBulk(selectedRows.map((r) => ({
      nameId: r.nameId,
      ...blankDecision(),
      action: 'skip',
      skipReason: bulkSkipReason,
      expectedVersion: r.rowVersion,
    })), { clearSelection: true });
  };

  const handleApply = () => {
    if (undecided.count > 0 || unsavedCount > 0) return;
    doApply('all');
  };

  const handleApplyDecidedSkipRest = () => {
    if (unsavedCount > 0) return;
    if (undecided.count === 0) {
      doApply('all');
      return;
    }
    const ok = window.confirm(`${undecided.count} name${undecided.count === 1 ? '' : 's'} (${money(undecided.amount)}) will be left for Not in Menu.`);
    if (ok) doApply('decided_skip_rest');
  };

  const changeGroup = (key) => {
    setActiveGroup(key);
    setPanel(null);
    setSelected(new Set());
  };

  const scrollRowIntoView = (nameId) => {
    window.requestAnimationFrame(() => {
      const el = document.getElementById(`snc-row-${nameId}`);
      if (el) el.scrollIntoView({ block: 'nearest' });
    });
  };

  const rowActions = {
    focus: (row) => setFocusedId(row.nameId),
    toggleSelect: (row) => setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(row.nameId)) next.delete(row.nameId);
      else next.add(row.nameId);
      return next;
    }),
    link: clickLink,
    add: clickAdd,
    skip: clickSkip,
    closePanel: () => setPanel(null),
    pickDish: (row, menuItemId) => {
      setPanel(null);
      saveRow(row, { ...blankDecision(), action: 'link', targetMenuItemId: Number(menuItemId) });
    },
    saveAdd: (row, addName, addPrice) => {
      setPanel(null);
      saveRow(row, { ...blankDecision(), action: 'add', addName, addPrice });
    },
    saveSkip: (row, skipReason) => {
      setPanel(null);
      saveRow(row, { ...blankDecision(), action: 'skip', skipReason });
    },
    acceptSplit: (row) => {
      const proposal = (row.flags && row.flags.sizeSplitProposal) || [];
      saveRow(row, {
        ...blankDecision(),
        action: 'link',
        priceSplit: proposal.map((p) => ({ price: Number(p.price), menuItemId: Number(p.menuItemId) })),
      });
    },
    setLinkOption: (row, patch) => {
      const base = row.decision && row.decision.action === 'link'
        ? decisionBody(row)
        : { ...blankDecision(), action: 'link', targetMenuItemId: row.matchedMenuItem ? Number(row.matchedMenuItem.menuItemId) : null };
      if (!base.targetMenuItemId) return;
      saveRow(row, { ...base, ...patch });
    },
    loadAddCheck: (row) => addCheck(row.nameId),
    retry: (row) => {
      const ui = rowUiRef.current[row.nameId];
      if (ui && ui.body) saveRow(row, ui.body);
    },
    replaceConflict: resolveReplace,
    keepExisting: resolveKeepExisting,
    undoApply: handleResetRow,
  };

  // ── Keyboard: L / A / S act on the focused row, J / K move (spec §12) ─────

  keyHandlerRef.current = (e) => {
    if (phase !== 'worklist' || prompt || conflict) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || (t && t.isContentEditable)) return;
    const key = String(e.key || '').toLowerCase();
    if (key === 'j' || key === 'k') {
      if (visibleRows.length === 0) return;
      e.preventDefault();
      const idx = visibleRows.findIndex((r) => r.nameId === focusedId);
      let nextIdx = 0;
      if (idx !== -1) nextIdx = key === 'j' ? Math.min(idx + 1, visibleRows.length - 1) : Math.max(idx - 1, 0);
      const target = visibleRows[nextIdx];
      setFocusedId(target.nameId);
      scrollRowIntoView(target.nameId);
      return;
    }
    if (key !== 'l' && key !== 'a' && key !== 's') return;
    const row = visibleRows.find((r) => r.nameId === focusedId);
    if (!row) return;
    e.preventDefault();
    if (key === 'l') clickLink(row);
    else if (key === 'a') clickAdd(row);
    else clickSkip(row);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (keyHandlerRef.current) keyHandlerRef.current(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderPrompt = () => {
    if (!prompt) return null;
    const cancel = () => setPrompt(null);
    if (prompt.type === 'columns') {
      return (
        <ColumnConfirmModal
          fileName={prompt.file.name}
          sample={prompt.sample}
          guesses={prompt.guesses}
          headerRowIndex={prompt.headerRowIndex}
          busy={uploading}
          onCancel={cancel}
          onConfirm={(columnMap) => runUpload(prompt.file, { ...prompt.answers, columnMap })}
        />
      );
    }
    if (prompt.type === 'month') {
      return (
        <MonthPickerModal
          fileName={prompt.file.name}
          busy={uploading}
          onCancel={cancel}
          onConfirm={(month) => runUpload(prompt.file, { ...prompt.answers, month })}
        />
      );
    }
    return (
      <OverlapModal
        fileName={prompt.file.name}
        existing={prompt.existing}
        busy={uploading}
        onCancel={cancel}
        onReplace={() => runUpload(prompt.file, { ...prompt.answers, overlap: 'replace' })}
        onAdd={() => runUpload(prompt.file, { ...prompt.answers, overlap: 'add' })}
      />
    );
  };

  const renderWorklist = () => (
    <>
      <section className="snc-card">
        <WorklistHeader
          uploads={meta ? meta.uploads : []}
          ignoredRows={meta ? meta.ignoredRows : []}
          coverage={meta ? meta.coverage : null}
          uploading={uploading}
          uploadError={uploadError}
          busy={busy}
          onAddFile={(file) => runUpload(file)}
          onRemoveUpload={handleRemoveUpload}
          groupCounts={groupCounts}
          activeGroup={activeGroup}
          onGroup={changeGroup}
          search={search}
          onSearch={setSearch}
          undecided={undecided}
          conflictCount={conflictCount}
        >
          {activeGroup === 'confident' && acceptable.length > 0 && (
            <button type="button" className="snc-btn snc-btn--primary snc-btn--small" onClick={handleAcceptAll} disabled={busy}>
              Accept all ({acceptable.length})
            </button>
          )}
        </WorklistHeader>
        <p className="snc-keys-hint">Keys: J / K move · L link · A add · S skip</p>

        {selectedRows.length > 0 && (
          <div className="snc-bulkbar">
            <span className="snc-bulkbar__count">{selectedRows.length} selected</span>
            <select
              className="snc-select"
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              aria-label="Skip reason for the selected names"
            >
              <option value="">{bulkNeedsReason ? 'Choose a reason…' : 'Reason (optional)'}</option>
              {SKIP_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {bulkReason === 'other' && (
              <input
                className="snc-input"
                placeholder="What are they?"
                value={bulkReasonText}
                onChange={(e) => setBulkReasonText(e.target.value)}
                aria-label="Other reason"
              />
            )}
            <button type="button" className="snc-action" onClick={handleSkipSelected} disabled={!canBulkSkip}>
              Skip selected
            </button>
            <button type="button" className="snc-link-btn" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        <div className="snc-table">
          <div className="snc-table__head">
            <span>POS name</span>
            <span className="snc-num">Plates</span>
            <span>Suggestion</span>
            <span className="snc-actions-head">Action</span>
          </div>
          {visibleRows.length === 0 && (
            <p className="snc-empty">{q ? `No names match “${search.trim()}”.` : 'Nothing in this group.'}</p>
          )}
          {visibleRows.map((row) => (
            <NameRow
              key={row.nameId}
              row={row}
              ui={rowUi[row.nameId] || null}
              focused={focusedId === row.nameId}
              selected={selected.has(row.nameId)}
              panel={panel && panel.nameId === row.nameId ? panel.kind : null}
              menu={menu}
              preferIds={unsoldIds}
              labelFor={labelFor}
              pageBusy={busy}
              actions={rowActions}
            />
          ))}
        </div>

        <div className="snc-worklist-footer">
          {undecided.count > 0 ? (
            <button type="button" className="snc-link-btn" onClick={handleApplyDecidedSkipRest} disabled={busy || unsavedCount > 0}>
              Apply what's decided and skip the rest
            </button>
          ) : <span />}
          <button
            type="button"
            className="snc-btn snc-btn--primary"
            onClick={handleApply}
            disabled={busy || undecided.count > 0 || unsavedCount > 0}
            title={undecided.count > 0 ? `${undecided.count} name${undecided.count === 1 ? '' : 's'} still need a decision` : undefined}
          >
            {busy ? 'Working…' : 'Apply'}
          </button>
        </div>
        {undecided.count > 0 && (
          <p className="snc-pending-note">
            {undecided.count} name{undecided.count === 1 ? '' : 's'} still need a decision.
          </p>
        )}
        {unsavedCount > 0 && (
          <p className="snc-pending-note snc-pending-note--error">Some choices are not saved yet — retry them before Apply.</p>
        )}
      </section>

      <div className="snc-footer snc-footer--page">
        <button type="button" className="snc-btn snc-btn--outline" onClick={handleSetAside} disabled={busy}>
          Set aside
        </button>
        <div className="snc-footer__right">
          <span className="snc-footer__hint">{conflictCount > 0 ? 'Resolve conflicts to continue' : 'Unlocks after Apply'}</span>
          <button type="button" className="snc-btn snc-btn--locked" disabled>
            <Lock size={13} /> Continue
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="snc-page">
      <header className="snc-header">
        <div className="snc-shell snc-header__inner">
          <button type="button" className="snc-back-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <h1>Sales Name Check</h1>
        </div>
      </header>

      <main className="snc-shell snc-main">
        {notice && (
          <div className={`snc-notice snc-notice--${notice.tone}`} role={notice.tone === 'error' ? 'alert' : 'status'}>
            <span>{notice.text}</span>
            <button type="button" className="snc-icon-btn" onClick={() => setNotice(null)} aria-label="Dismiss">
              <X size={14} />
            </button>
          </div>
        )}

        {phase === 'loading' && <p className="snc-loading">Loading…</p>}

        {phase === 'error' && (
          <section className="snc-card">
            <p className="snc-error" role="alert">{loadError}</p>
            <div className="snc-footer">
              <span />
              <button type="button" className="snc-btn snc-btn--primary" onClick={loadStatus}>Retry</button>
            </div>
          </section>
        )}

        {phase === 'locked' && (
          <section className="snc-card">
            <div className="snc-card__title-row">
              <Lock size={15} className="snc-lock-icon" />
              <h2>Finish Item Mapping first</h2>
            </div>
            <p className="snc-card__subtitle">
              {(status && status.lockedReason) || 'Every recipe must be confirmed in Item Mapping before your sales names can be checked.'}
            </p>
            <div className="snc-footer">
              <span />
              <button type="button" className="snc-btn snc-btn--primary" onClick={() => onOpenItemMap && onOpenItemMap(null)}>
                Open Item Map
              </button>
            </div>
          </section>
        )}

        {phase === 'upload' && (
          <UploadCard
            mode={mode}
            uploading={uploading}
            uploadError={uploadError}
            busy={busy}
            onFile={(file) => runUpload(file)}
            onSkip={handleSkipStep}
          />
        )}

        {phase === 'worklist' && renderWorklist()}

        {phase === 'closed' && (
          <ContinueScreen
            mode={mode}
            checkState={closedState}
            summary={applySummary}
            busy={busy}
            onContinue={onContinue}
            onDone={onBack}
            onRunAgain={handleRunAgain}
            onOpenItemMap={onOpenItemMap}
          />
        )}
      </main>

      {renderPrompt()}

      {conflict && (
        <ConflictModal
          name={conflict.row.displayName}
          current={conflict.current}
          busy={busy}
          onCancel={async () => {
            setConflict(null);
            await loadWorklist();
          }}
          onReplace={async () => {
            const c = conflict;
            setConflict(null);
            patchRowUi(c.row.nameId, null);
            await saveRow(c.row, c.body, { expectedVersion: c.current.rowVersion });
          }}
        />
      )}
    </div>
  );
}

export default SalesNameCheck;
